import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import { fullName } from 'shared/models/user';
import { postRating } from 'components/product/actions';
import { TextButton, LoadingButton } from 'scy/buttons';
import StarRatingInput from 'scy/StarRatingInput';
import { Warning } from 'scy/Warning';
import { body } from 'scy/text/styles.mod.scss';
import padding from 'scy/padding.mod.scss';
import margin from 'scy/margin.mod.scss';
import border from 'scy/border.mod.scss';
import styles from './Form.mod.scss';

export default function ReviewForm({ user, sku, onCancel, onSuccess }) {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState(fullName(user) || '');
  const [email, setEmail] = useState(user.email || '');
  const [opinion, setOpinion] = useState('');
  const [details, setDetails] = useState('');
  const [stars, setStars] = useState(1);
  const [recommend, setRecommend] = useState(true);

  function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!stars) return setError('Your rating is required');
    if (!opinion) return setError('Your one-sentence opinion is required');

    setLoading(true);

    return dispatch(
      postRating(sku, {
        name,
        email,
        rating: stars,
        review: opinion,
        metadata: {
          details,
          recommend
        }
      })
    )
      .then(() => {
        setLoading(false);
        onSuccess();
      })
      .catch(error => {
        setLoading(false);
        setError(error.message);
      });
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={`${body} ${padding.top_md} ${padding.bottom_sm} ${
        border.bottomMedium
      } ${margin.bottom_lg}`}
    >
      <div className={`${styles.inputContainer} ${padding.tablet_right_md}`}>
        <label htmlFor="fullname">What’s your name?</label>
        <input
          id="fullname"
          className={styles.input}
          placeholder="We never show your full name"
          autoFocus={!name}
          value={name}
          onChange={e => setName(e.target.value)}
        />
      </div>

      <div className={styles.inputContainer}>
        <label htmlFor="email">Your email?</label>
        <input
          id="email"
          className={styles.input}
          placeholder="We use this to verify your purchase"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />
      </div>

      <div className={styles.inputContainer}>
        <label>
          How would you rate this product?
          <StarRatingInput
            selected={stars}
            handleRating={setStars}
            className={styles.starRating}
          />
        </label>
      </div>

      <div className={styles.inputContainer}>
        <div>Would you recommend it?</div>
        <div className={styles.toggle}>
          <input
            name="recommend"
            id="wouldRecommend"
            type="radio"
            className={styles.radio}
            onChange={() => setRecommend(true)}
          />
          <label
            htmlFor="wouldRecommend"
            className={classnames(styles.toggleLabel, {
              [styles.selectedToggleLabel]: recommend
            })}
          >
            Yes
          </label>
          <input
            name="recommend"
            id="wouldNotRecommend"
            type="radio"
            className={styles.radio}
            onChange={() => setRecommend(false)}
          />
          <label
            htmlFor="wouldNotRecommend"
            className={classnames(styles.toggleLabel, {
              [styles.selectedToggleLabel]: recommend !== null && !recommend
            })}
          >
            No
          </label>
        </div>
      </div>

      <label htmlFor="opinion">Your opinion in one sentence</label>
      <input
        id="opinion"
        className={styles.input}
        placeholder="The smoothest sip out there…"
        required
        value={opinion}
        onChange={e => setOpinion(e.target.value)}
      />

      <label htmlFor="details">Now share some details</label>
      <textarea
        id="details"
        placeholder="Tell potential sippers why you feel that way…"
        className={styles.textarea}
        value={details}
        onChange={e => setDetails(e.target.value)}
      />

      <div className={styles.formButtons}>
        <LoadingButton
          type="submit"
          loading={loading}
          className={styles.submitButton}
        >
          Submit
        </LoadingButton>
        <TextButton
          noUnderline
          type="button"
          onClick={onCancel}
          disabled={loading}
          className={styles.cancelButton}
        >
          Cancel
        </TextButton>
      </div>

      {error && <Warning>{error}</Warning>}
    </form>
  );
}
